import React from "react";
import { useTranslation } from "react-i18next";
import { FiArrowRight, FiHome, FiUsers } from "react-icons/fi";
import ParentPortalPageTemplate from "../../components/ParentPortalPageTemplate";

type FamilySelectorChild = {
    id?: string | number;
    uuid?: string;
};

export type SelectableFamily = {
    id?: string | number;
    uuid?: string;
    familyName?: string | null;
    parent1FirstName?: string | null;
    parent1LastName?: string | null;
    parent2FirstName?: string | null;
    parent2LastName?: string | null;
    children?: FamilySelectorChild[] | null;
};

type PpFamilySelectorProps = {
    families?: SelectableFamily[];
    onSelect?: (family: SelectableFamily) => void;
};

const getFamilyDisplayName = (family: SelectableFamily, fallback: string) => {
    const raw = family?.familyName || fallback;
    return String(raw || "").trim();
};

const getPartnerName = (family: SelectableFamily) => {
    const firstName = family?.parent2FirstName || family?.parent1FirstName || "";
    const lastName = family?.parent2LastName || family?.parent1LastName || "";
    return `${firstName} ${lastName}`.trim();
};

const PpFamilySelector = ({ families = [], onSelect }: PpFamilySelectorProps) => {
    const { t } = useTranslation();

    const pageTitle = t("parentPortal.choose_family_title", "Choose family");
    const introTitle = t("parentPortal.choose_family_intro_title", "Select your family");
    const introText = t(
        "parentPortal.choose_family_intro_text",
        "You are linked to multiple families. Choose which family you want to open."
    );

    return (
        <ParentPortalPageTemplate title={pageTitle}>
            <div className="mx-auto w-full max-w-4xl px-3 py-4 sm:px-6 sm:py-8">
                <section className="overflow-hidden rounded-[2rem] border border-white/60 bg-white/75 shadow-xl shadow-emerald-900/5 backdrop-blur dark:border-slate-700/70 dark:bg-slate-950/55">
                    <div className="border-b border-emerald-100/80 bg-gradient-to-br from-emerald-50/90 via-white/80 to-teal-50/80 px-5 py-5 dark:border-slate-700/70 dark:from-slate-900/80 dark:via-slate-950/70 dark:to-emerald-950/30 sm:px-7 sm:py-6">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0">
                                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200/80 bg-white/70 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 shadow-sm dark:border-emerald-800/80 dark:bg-emerald-950/30 dark:text-emerald-200">
                                    <FiHome className="h-3.5 w-3.5" />
                                    {t("parentPortal.family", "Family")}
                                </div>
                                <h1 className="mt-3 text-2xl font-black tracking-tight text-slate-900 dark:text-white sm:text-3xl">
                                    {introTitle}
                                </h1>
                                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300 sm:text-base">
                                    {introText}
                                </p>
                            </div>

                            <div className="inline-flex shrink-0 items-center gap-2 rounded-2xl border border-emerald-100 bg-white/80 px-4 py-3 text-sm font-bold text-emerald-700 shadow-sm dark:border-emerald-900/60 dark:bg-slate-900/70 dark:text-emerald-200">
                                <FiUsers className="h-5 w-5" />
                                <span>
                                    {families.length} {families.length === 1
                                        ? t("parentPortal.family_singular", "family")
                                        : t("parentPortal.family_plural", "families")}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-3 p-4 sm:p-6">
                        {families.length === 0 ? (
                            <div className="rounded-3xl border border-dashed border-slate-200 bg-white/70 px-5 py-8 text-center text-sm font-semibold text-slate-500 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-400">
                                {t("parentPortal.no_families_found", "No families found.")}
                            </div>
                        ) : (
                            families.map((family, index) => {
                                const familyName = getFamilyDisplayName(
                                    family,
                                    t("parentPortal.family_fallback_name", "Family")
                                );
                                const partnerName = getPartnerName(family);
                                const childCount = family.children?.length || 0;

                                return (
                                    <button
                                        key={family.id || family.uuid || index}
                                        type="button"
                                        onClick={() => onSelect?.(family)}
                                        className="group flex w-full items-center justify-between gap-4 rounded-3xl border border-slate-200/80 bg-white/85 px-4 py-4 text-left shadow-sm shadow-slate-900/5 transition duration-200 hover:-translate-y-0.5 hover:border-emerald-200 hover:bg-emerald-50/60 hover:shadow-lg hover:shadow-emerald-900/10 focus:outline-none focus:ring-4 focus:ring-emerald-100 dark:border-slate-700/80 dark:bg-slate-900/70 dark:hover:border-emerald-800 dark:hover:bg-emerald-950/20 dark:focus:ring-emerald-900/30 sm:px-5 sm:py-5"
                                    >
                                        <div className="flex min-w-0 items-center gap-4">
                                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100 transition group-hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-900/60">
                                                <FiHome className="h-5 w-5" />
                                            </div>

                                            <div className="min-w-0">
                                                <div className="truncate text-base font-black text-slate-900 dark:text-white sm:text-lg">
                                                    {familyName}
                                                </div>

                                                {partnerName && (
                                                    <div className="mt-1 truncate text-sm font-medium text-slate-500 dark:text-slate-300">
                                                        {partnerName}
                                                    </div>
                                                )}

                                                <div className="mt-2 inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                                    {childCount} {childCount === 1
                                                        ? t("parentPortal.child_singular", "child")
                                                        : t("parentPortal.child_plural", "children")}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm ring-1 ring-slate-200 transition group-hover:bg-emerald-600 group-hover:text-white group-hover:ring-emerald-600 dark:bg-slate-950/70 dark:text-slate-400 dark:ring-slate-700 dark:group-hover:bg-emerald-600 dark:group-hover:text-white dark:group-hover:ring-emerald-600">
                                            <FiArrowRight className="h-5 w-5" />
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </section>
            </div>
        </ParentPortalPageTemplate>
    );
};

export default PpFamilySelector;
